import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
  type ExportJournalDetails,
  type ExportJournalPeriod,
  type JournalReconReportMeta
} from '../../api-schema-types/financial/JournalReconDomainDtos'
import CONFIG from '../../app-configs'
import { ApiAuthStrategies } from '../shared/ApiAuthStrategies'

export interface DownloadReportArgs {
  startDate: string
  reportName: string
}

export const journalReconApi = createApi({
  reducerPath: 'journalReconApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${CONFIG.PROXIMA_BACK_END_V2_BASE_API_URL}/ledgers/journal-recons`,
    prepareHeaders: (headers: Headers) => {
      headers.set('authorization', ApiAuthStrategies.getBearerTokenHeader())
      return headers
    }
  }),

  tagTypes: ['JournalReconPeriods', 'JournalReconPeriod'],

  endpoints: (builder) => ({
    getPeriods: builder.query<ExportJournalPeriod[], void>({
      query: () => ({ url: '/periods', method: 'GET' }),
      providesTags: ['JournalReconPeriods']
    }),

    getPeriodDetails: builder.query<ExportJournalDetails[], string>({
      query: (startDate: string) => ({ url: `/periods/${startDate}`, method: 'GET' }),
      providesTags: (_result, _error, startDate) => [{ type: 'JournalReconPeriod', id: startDate }]
    }),

    listReports: builder.query<JournalReconReportMeta[], void>({
      query: () => ({ url: '/reports', method: 'GET' })
    }),

    openPeriod: builder.mutation<ExportJournalDetails[], string>({
      query: (startDate: string) => ({
        url: '/open-period',
        method: 'POST',
        body: { startDate }
      }),
      invalidatesTags: (_result, _error, startDate) => [
        'JournalReconPeriods',
        { type: 'JournalReconPeriod', id: startDate }
      ]
    }),

    closePeriod: builder.mutation<ExportJournalDetails[], string>({
      query: (startDate: string) => ({
        url: '/close-period',
        method: 'POST',
        body: { startDate }
      }),
      invalidatesTags: (_result, _error, startDate) => [
        'JournalReconPeriods',
        { type: 'JournalReconPeriod', id: startDate }
      ]
    }),

    downloadReportCsv: builder.query<{ csv: string, filename: string }, DownloadReportArgs>({
      query: ({ startDate, reportName }) => ({
        url: `/periods/${startDate}/exports/${reportName}`,
        method: 'GET',
        responseHandler: async (response: Response) => {
          const csv = await response.text()
          const filename = response.headers.get('X-Proxima-CSV-filename') ?? `${reportName}-${startDate}.csv`
          return { csv, filename }
        }
      })
    }),

    downloadAllReportsZip: builder.query<{ blob: Blob, filename: string }, string>({
      query: (startDate: string) => ({
        url: `/periods/${startDate}/exports.zip`,
        method: 'GET',
        responseHandler: async (response: Response) => {
          const blob = await response.blob()
          const filename = response.headers.get('X-Proxima-Zip-filename') ?? `recon_export_journals-${startDate}.zip`
          return { blob, filename }
        }
      })
    })
  })
})

export const useGetJournalReconPeriodsQuery = journalReconApi.endpoints.getPeriods.useQuery
export const useGetJournalReconPeriodDetailsQuery = journalReconApi.endpoints.getPeriodDetails.useQuery
export const useListJournalReconReportsQuery = journalReconApi.endpoints.listReports.useQuery
export const useOpenJournalReconPeriodMutation = journalReconApi.endpoints.openPeriod.useMutation
export const useCloseJournalReconPeriodMutation = journalReconApi.endpoints.closePeriod.useMutation
export const useDownloadJournalReconReportCsvLazyQuery = journalReconApi.endpoints.downloadReportCsv.useLazyQuery
export const useDownloadJournalReconAllReportsZipLazyQuery = journalReconApi.endpoints.downloadAllReportsZip.useLazyQuery
